import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { CommunitiesService, CommunityService, UserService, buildProfileSearchFields, communityHasOnboardingFlow, environment } from "@mypxplat/xplat/core";
import { CommunityBaseComponent } from "@mypxplat/xplat/features";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { take } from "rxjs/operators";

import { AddEditSkillComponent } from "../../modals";

/**
 * The registry moved to `community.model.ts` so the NativeScript app runs the same list.
 * Re-exported here because every existing web import points at this file.
 */
export { communityHasOnboardingFlow };

type SetupStep = "init" | "profile" | "community_specific";

/**
 * Per-community onboarding wizard. Three-step flow:
 *
 *   1. `init` — SHARED. Welcome heading + "Turn It On" (public) / "Keep Me Hidden" choice.
 *      Writes the profile mirror with `public` + basic identity fields; the per-community
 *      setup-complete flag is written here so the wizard doesn't re-fire on refresh even
 *      if the user abandons before finishing.
 *   2. `profile` — SHARED. Tagline + description (+ optional first/last name if missing).
 *      Skipped when the user already has both tagline and description filled in, and also
 *      skipped for "Keep Me Hidden" users who chose not to be publicly visible.
 *   3. `community_specific` — PER-COMMUNITY. Whatever unique welcome content this
 *      community wants — FSP shows the four-icon summary + a skills-picker prompt; TMP
 *      shows a "what's this community about" paragraph and a Connect-Discord CTA.
 *
 * Add a new community's flow by adding its string_id to the registry AND an
 * `@if (communityStringId == "...")` block in the community_specific section of the
 * template.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-onboarding-wizard",
  templateUrl: "community-onboarding-wizard.component.html",
  standalone: false,
})
export class CommunityOnboardingWizardComponent extends CommunityBaseComponent implements OnInit {
  @Input() communityStringId: string;
  @Output() complete = new EventEmitter<void>();

  public setupStep: SetupStep = "init";
  public env = environment;

  /** Editable profile draft — bound to the tagline/description inputs on the profile step. */
  public profile: any = {
    description: "",
    tagline: "",
  };

  /** Human-readable community title, resolved once at mount. Falls back to the raw stringId. */
  public communityTitle: string = "";

  constructor(
    userService: UserService,
    communityService: CommunityService,
    public webCommunityService: WebCommunityService,
    public appService: AppService,
    private communitiesService: CommunitiesService,
    private cd: ChangeDetectorRef
  ) {
    super(userService, communityService);
  }

  ngOnInit() {
    // Seed the editable draft from the cached community profile — same source
    // `<myp-community>` used before the wizard was extracted.
    this.profile.description = this.webCommunityService?.profile?.description || this.userService.user?.description || "";
    this.profile.tagline = this.webCommunityService?.profile?.tagline || "";

    this.resolveCommunityTitle();

    // Skills list drives the FSP branch of the community_specific step. Loading here means
    // "you already have N skills" state is correct if the user restarts partway through.
    // Non-FSP flows just don't render the skills block, so the extra call is cheap and harmless.
    if (this.userService.user) {
      this.loadMySkills().subscribe();
    }
    // TMP's Discord control is `<myp-account-connections [compact]="true">` embedded inline —
    // it fetches its own linked state on mount, so no extra plumbing needed here.
  }

  /**
   * Pull the community's admin-set title from CommunitiesService so the welcome heading
   * reads e.g. "Welcome to Tone Master Pro" instead of hardcoded FSP copy. Falls back to
   * the raw stringId if the service hasn't loaded yet or the community isn't in the cache.
   */
  private resolveCommunityTitle() {
    const cached = this.communitiesService.getCommunity(this.communityStringId);
    if (cached?.title) {
      this.communityTitle = cached.title;
      return;
    }
    this.communitiesService.load();
    this.communitiesService.communities$.pipe(take(1)).subscribe(() => {
      const community = this.communitiesService.getCommunity(this.communityStringId);
      this.communityTitle = community?.title || this.communityStringId;
      this.cd.detectChanges();
    });
    this.communityTitle = this.communityStringId;
  }

  /** Whether the user still has a tagline or description to fill in on the shared profile step. */
  private profileNeedsFilling(): boolean {
    const p = this.webCommunityService?.profile;
    const tagline = (p?.tagline || "").toString().trim();
    const description = (p?.description || this.userService.user?.description || "").toString().trim();
    // Truthy AND still show the step when EITHER is empty — matches the user's ask:
    // "if you havent entered a tagline or description, we should show that."
    return !tagline || !description;
  }

  /**
   * Step 1: user chose Public or Hidden. Writes the profile mirror and marks setup complete
   * so the wizard doesn't re-open on a mid-flow refresh. Skips the shared profile step for
   * hidden users (no visible profile → no reason to fill it in) and for public users who
   * already have both tagline and description.
   */
  setProfile(isPublic: boolean) {
    const args = {
      photo: this.userService.user.photoURL,
      cover_photo: this.userService.user.cover_photo,
      public: isPublic,
      firstName: this.userService.user.firstName,
      lastName: this.userService.user.lastName,
      // Search keys for the directory and @mention search. Derived in one place
      // so they can't drift apart or be skipped (see buildProfileSearchFields).
      ...buildProfileSearchFields({ firstName: this.userService.user.firstName, lastName: this.userService.user.lastName }),
      description: this.userService.user.description,
      id: this.userService.user.id,
      dateCommunityActivated: new Date(),
      email: this.userService.user.email.toLowerCase(),
    };

    // Fire the profile write and the setup-complete flag write in parallel — they touch
    // disjoint fields, and neither depends on the other's outcome to advance the wizard.
    this.webCommunityService.updateProfile(args);
    this.webCommunityService.markCommunitySetupComplete(this.communityStringId);
    this.userService.saveUser({ public: isPublic }).subscribe();

    // Advance: hidden users go straight to the per-community step; public users go there
    // too if their profile is already filled in.
    if (!isPublic || !this.profileNeedsFilling()) {
      this.setupStep = "community_specific";
    } else {
      this.setupStep = "profile";
    }
    this.cd.detectChanges();
  }

  /**
   * Step 2: save the shared profile fields (tagline + description + optional first/last)
   * and advance to the per-community step. The "Do this later" affordance in the template
   * calls this too — the promise still fires with whatever's in `this.profile`, which is
   * a no-op if the user didn't type anything.
   */
  updateProfile() {
    this.webCommunityService.updateProfile(this.profile);
    if (this.profile.description || this.profile.firstName || this.profile.lastName) {
      // Tagline lives on the community profile only (Firestore), not on the base Customer
      // row — omit it from the paeapi save so the API doesn't reject an unknown column.
      const userPatch = { ...this.profile };
      delete userPatch.tagline;
      this.userService.saveUser(userPatch).subscribe();
    }
    this.setupStep = "community_specific";
    this.cd.detectChanges();
  }

  /** FSP-only: opens the AddEditSkill modal from the community_specific step. */
  addSkill() {
    const modalRef = this.appService.showModal(AddEditSkillComponent, { size: "lg" });
    modalRef.componentInstance.skillAdded.pipe(take(1)).subscribe(() => {
      this.loadMySkills().subscribe();
    });
  }

  /**
   * Community-specific step's "Let's go" button — bubble up so the parent dismisses the
   * overlay. `markCommunitySetupComplete` has already committed on step 1, so no extra
   * write is needed here.
   */
  finish() {
    this.complete.emit();
  }
}
